import React, { useEffect, useMemo, useState } from "react";
import { Save, Plus, Trash2, Loader2, ArrowUp, ArrowDown, Eye, EyeOff, Link2, BarChart3, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { iconNames } from "../../lib/icons";
import PackageServicesTab from "./PackageServicesTab";
import PackageJourneyModal from "./PackageJourneyModal";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
// Comma-separated lists, so a list of question keys is one field rather than a form.
const csv = (arr) => (arr || []).join(", ");
const list = (value) => value.split(",").map((s) => s.trim()).filter(Boolean);

const Field = ({ label, value, onChange, area, rows = 2, placeholder, hint }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">{label}</span>
    {area ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
    ) : (
      <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
    )}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

const Card = ({ title, children, subtitle }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
    <h2 className="font-display font-bold text-navy">{title}</h2>
    {subtitle && <p className="text-xs text-slate-500 mt-1 mb-4">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className="font-display font-extrabold text-navy text-2xl mt-1">{value}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

// Simple ordered list editor for strings or {} items.
const ListEditor = ({ items, onChange, makeEmpty, renderItem, addLabel, testPrefix }) => {
  const move = (i, d) => {
    const next = [...items];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {(items || []).map((item, i) => (
        <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4 relative" data-testid={`${testPrefix}-${i}`}>
          <div className="absolute top-3 right-3 flex items-center gap-1">
            <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-navy" title="Move up" data-testid={`${testPrefix}-up-${i}`}><ArrowUp size={14} /></button>
            <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-navy" title="Move down" data-testid={`${testPrefix}-down-${i}`}><ArrowDown size={14} /></button>
            <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-red-400 hover:text-red-600 ml-1" title="Remove" data-testid={`${testPrefix}-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          <div className="pr-20">{renderItem(item, (patch) => onChange(items.map((it, x) => (x === i ? patch : it))), i)}</div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...(items || []), makeEmpty()])} className="rounded-full" data-testid={`${testPrefix}-add`}>
        <Plus size={14} className="mr-1" /> {addLabel}
      </Button>
    </div>
  );
};

const VISIBILITY = [
  { key: "live", label: "Live", icon: Eye, desc: "On the site, in the main menu and in the sitemap for Google." },
  { key: "hidden", label: "Hidden — link only", icon: Link2, desc: "Works fully for anyone with the direct link, but off the menu, off service pages, out of the sitemap and told not to index. Good for showing a few people first." },
  { key: "off", label: "Off completely", icon: EyeOff, desc: "Not available at all. Visitors see your short notice, and the pricing tool is switched off so an old link can't still give a price." },
];

const SECTION_LABELS = {
  builder: ["The package builder tool", "Where visitors pick services, answer the questions and see their price."],
  covers: ["What a CASS package covers", "The six explanatory cards."],
  explainer: ["How we price", "The 'priced on the work, not your postcode' block."],
  discovery: ["What happens on a Discovery Call", "The three numbered steps."],
  faqs: ["FAQs", "Also sent to Google as FAQ structured data."],
  reviews: ["Google reviews", "Your live review carousel — it brings its own styling."],
  cta: ["Closing call to action", "The 'Rather just talk to someone?' block at the foot of the page."],
};

// Only the three backgrounds the rest of the site uses, so a page can't drift off-brand.
const BACKGROUNDS = [
  { key: "white", label: "White", swatch: "#FFFFFF" },
  { key: "light", label: "Light grey", swatch: "#E6ECF3" },
  { key: "navy", label: "Navy (dark)", swatch: "#0B1A2F" },
];
const BG_LABEL = Object.fromEntries(BACKGROUNDS.map((b) => [b.key, b.label]));

const Choice = ({ on, onClick, children, testId, title }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${
      on ? "bg-navy text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
    }`}
  >
    {children}
  </button>
);

const SectionOrder = ({ items, onChange }) => {
  const move = (i, d) => {
    const next = [...items];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const patch = (i, p) => onChange(items.map((x, j) => (j === i ? { ...x, ...p } : x)));
  // Only sections a visitor actually sees can clash, so hidden ones are skipped.
  const visible = items.filter((s) => s.on !== false && s.bg);
  const clashes = new Set(
    visible.filter((s, i) => i > 0 && visible[i - 1].bg === s.bg).map((s) => s.key),
  );

  return (
    <div className="space-y-2" data-testid="package-section-order">
      {items.map((s, i) => {
        const [label, hint] = SECTION_LABELS[s.key] || [s.key, ""];
        const on = s.on !== false;
        const styled = !!s.bg;
        return (
          <div
            key={s.key}
            data-testid={`package-section-${s.key}`}
            className={`rounded-xl border p-4 ${on ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50"}`}
          >
            <div className="flex items-start gap-3">
              <span className="font-display font-extrabold text-slate-300 text-sm w-5 shrink-0 mt-0.5">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${on ? "text-navy" : "text-slate-400"}`}>{label}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{on ? hint : "Switched off — visitors won't see this."}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => patch(i, { on: !on })}
                  className={`p-1.5 rounded-lg ${on ? "text-teal hover:bg-teal/10" : "text-slate-400 hover:bg-slate-100"}`}
                  title={on ? "Switch off" : "Switch on"}
                  data-testid={`package-section-toggle-${s.key}`}
                >
                  {on ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30" title="Move up" data-testid={`package-section-up-${s.key}`}>
                  <ArrowUp size={15} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 disabled:opacity-30" title="Move down" data-testid={`package-section-down-${s.key}`}>
                  <ArrowDown size={15} />
                </button>
              </div>
            </div>

            {on && styled && (
              <div className="mt-3 pl-8 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Background</span>
                  {BACKGROUNDS.filter((b) => !(s.key === "builder" && b.key === "navy")).map((b) => (
                    <button
                      key={b.key}
                      onClick={() => patch(i, { bg: b.key })}
                      title={b.label}
                      data-testid={`package-section-bg-${s.key}-${b.key}`}
                      style={{ backgroundColor: b.swatch }}
                      className={`h-6 w-6 rounded-full border border-slate-200 transition-all ${
                        s.bg === b.key ? "ring-2 ring-orange ring-offset-1" : "hover:scale-110"
                      }`}
                    />
                  ))}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Width</span>
                  <Choice on={s.width === "wide"} onClick={() => patch(i, { width: "wide" })} testId={`package-section-width-${s.key}-wide`} title="Full width">Wide</Choice>
                  <Choice on={s.width === "narrow"} onClick={() => patch(i, { width: "narrow" })} testId={`package-section-width-${s.key}-narrow`} title="Narrow, centred column">Narrow</Choice>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-slate-400 mr-0.5">Spacing</span>
                  <Choice on={s.pad === "normal"} onClick={() => patch(i, { pad: "normal" })} testId={`package-section-pad-${s.key}-normal`} title="Normal vertical spacing">Normal</Choice>
                  <Choice on={s.pad === "compact"} onClick={() => patch(i, { pad: "compact" })} testId={`package-section-pad-${s.key}-compact`} title="Tighter vertical spacing">Compact</Choice>
                </span>
                {s.key === "builder" && <span className="text-[11px] text-slate-400">The tool's panels need a light background.</span>}
              </div>
            )}

            {on && clashes.has(s.key) && (
              <p className="mt-3 pl-8 text-[11px] text-orange-dark flex items-start gap-1.5" data-testid={`package-section-clash-${s.key}`}>
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                Same {BG_LABEL[s.bg]?.toLowerCase()} background as the section above, so the two will run together as one block.
              </p>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-slate-400 pt-1">
        The title and intro at the top of the page are always the dark patterned band, so give the section directly beneath it a
        white or light grey background unless you want one long dark run.
      </p>
    </div>
  );
};

export default function PackageBuilderManager() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");
  const [insights, setInsights] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [health, setHealth] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [dropOff, setDropOff] = useState(null);

  useEffect(() => {
    adminApi.packageBuilder.getConfig().then(setForm).catch(() => {});
    adminApi.packageBuilder.insights().then(setInsights).catch(() => {});
    adminApi.packageBuilder.dropOff().then(setDropOff).catch(() => {});
    adminApi.packageBuilder.catalogue().then(setCatalogue).catch(() => {});
    adminApi.packageBuilder.health().then(setHealth).catch(() => setHealth({ ok: false, error: "Couldn't check the connection to the pricing tool." }));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIn = (section, k, v) => setForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.packageBuilder.saveConfig(form);
      setForm(updated);
      toast({ title: "Package page saved" });
    } catch {
      toast({ title: "Couldn't save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeJourney = async (sid) => {
    setDeletingId(sid);
    try {
      await adminApi.packageBuilder.removeSession(sid);
      // Refetch so every figure on the Insights screen drops this journey too.
      setInsights(await adminApi.packageBuilder.insights());
      setConfirmId(null);
      toast({ title: "Journey deleted", description: "The Insights figures have been worked out again without it." });
    } catch {
      toast({ title: "Couldn't delete that journey", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  // After a round of testing the figures are all ours, not real visitors'. This
  // clears what the website has recorded and nothing else.
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const resetInsights = async () => {
    setResetting(true);
    try {
      const r = await adminApi.packageBuilder.resetInsights();
      setInsights(await adminApi.packageBuilder.insights());
      setDropOff(await adminApi.packageBuilder.dropOff());
      setConfirmReset(false);
      toast({
        title: "Insights reset",
        description: `${r.sessions} journey${r.sessions === 1 ? "" : "s"} cleared. The figures start again from the next visitor.`,
      });
    } catch {
      toast({ title: "Couldn't reset the insights", variant: "destructive" });
    } finally {
      setResetting(false);
    }
  };


  const journeys = insights?.journeys || [];
  const t = insights?.totals || {};
  const w = insights?.walkaway || {};
  const maxBand = useMemo(
    () => Math.max(1, ...(w.bands || []).map((x) => x.walked_away + x.converted)),
    [w.bands],
  );
  const maxLost = useMemo(
    () => Math.max(1, ...((dropOff?.stages || []).map((s) => s.left))),
    [dropOff],
  );


  if (!form) return <p className="text-sm text-slate-400">Loading…</p>;

  const hero = form.hero || {};
  const covers = form.covers || {};
  const explainer = form.pricing_explainer || {};
  const discovery = form.discovery || {};
  const cta = form.cta || {};
  const builder = form.builder || {};
  const unavailable = form.unavailable || {};
  const walkaway = form.walkaway_offer || {};


  return (
    <div data-testid="package-builder-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">What Would CASS Cost?</h1>
          <p className="text-sm text-slate-500">Edit every word on the page, choose whether it's visible, and see what visitors do with it.</p>
        </div>
        {tab === "content" && (
          <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shrink-0" data-testid="package-page-save">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
          </Button>
        )}
      </div>

      {health && !health.ok && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 mb-6 flex items-start gap-3" data-testid="package-health-bad">
          <AlertTriangle size={19} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-navy text-sm">The builder can't reach the pricing tool, so visitors can't get a price</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{health.error}</p>
            {health.base && <p className="text-xs text-slate-400 mt-2">Currently pointed at: <span className="font-mono">{health.base}</span></p>}
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              On the live site this is usually the stored address being out of date: open <strong>Manage Publishes → Secrets</strong>,
              edit <span className="font-mono">CASS_API_BASE</span>, then re-publish. Until it's fixed, visitors see a polite notice
              and a link to contact you — not an error.
            </p>
          </div>
        </div>
      )}
      {health?.ok && (
        <p className="text-xs text-teal flex items-center gap-1.5 mb-6" data-testid="package-health-ok">
          <CheckCircle2 size={13} /> Connected to the pricing tool — {health.services} services, pricing version {health.pricing_version || "unknown"}.
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {[["content", "Visibility & content"], ["services", "What's offered"], ["insights", "Insights"], ["journeys", `Journeys (${journeys.length})`]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`package-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy hover:border-slate-400"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "content" && (
        <>
          <Card title="Visibility" subtitle="Controls where the page appears — and, at the strongest setting, whether the pricing tool answers at all.">
            <div className="grid md:grid-cols-3 gap-3">
              {VISIBILITY.map((v) => {
                const on = (form.visibility || "live") === v.key;
                return (
                  <button
                    key={v.key}
                    onClick={() => set("visibility", v.key)}
                    data-testid={`package-visibility-${v.key}`}
                    className={`text-left rounded-xl border p-4 transition-colors ${on ? "border-orange bg-orange/5" : "border-slate-200 hover:border-slate-400"}`}
                  >
                    <span className="flex items-center gap-2 font-bold text-navy text-sm"><v.icon size={15} /> {v.label}</span>
                    <span className="block text-xs text-slate-500 mt-2 leading-relaxed">{v.desc}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-4">
              The services and questions themselves come from the CASS Proposals pricing tool, so they aren't edited here — that's
              deliberate, so there's only ever one place a price can change.
            </p>
          </Card>

          <Card
            title="Order &amp; appearance of the page"
            subtitle="The order visitors scroll through, top to bottom, and how each block looks. The title and intro always come first. Text colour follows the background automatically, so a dark block gets light text."
          >
            <SectionOrder items={form.page_sections || []} onChange={(v) => set("page_sections", v)} />
          </Card>

          <Card title="Search engine listing">
            <div className="space-y-4">
              <Field label="Page title (SEO)" value={form.seo_title} onChange={(v) => set("seo_title", v)} />
              <Field label="Meta description (SEO)" value={form.seo_description} onChange={(v) => set("seo_description", v)} area rows={3} hint="Google shows about 160 characters." />
            </div>
          </Card>

          <Card title="Hero">
            <div className="space-y-4">
              <Field label="Eyebrow (small text above the heading)" value={hero.eyebrow} onChange={(v) => setIn("hero", "eyebrow", v)} />
              <Field label="Heading (H1)" value={hero.heading} onChange={(v) => setIn("hero", "heading", v)} />
              <Field label="Intro" value={hero.intro} onChange={(v) => setIn("hero", "intro", v)} area rows={3} />
              <div>
                <p className="text-xs font-semibold text-navy mb-2">Badge pills</p>
                <ListEditor
                  items={hero.badges || []}
                  onChange={(v) => setIn("hero", "badges", v)}
                  makeEmpty={() => ""}
                  addLabel="Add badge"
                  testPrefix="package-hero-badge"
                  renderItem={(item, patch) => <Field label="Text" value={item} onChange={patch} />}
                />
              </div>
            </div>
          </Card>

          <Card title="The indicative disclaimer" subtitle="Shown under the price, inside the 'How we price' section and reusable in your FAQs. This is the sentence that manages expectations if a Discovery Call changes the figure.">
            <Field label="Disclaimer" value={form.disclaimer} onChange={(v) => set("disclaimer", v)} area rows={3} />
          </Card>

          <Card title="Wording inside the builder" subtitle="The five step names and the headings a visitor sees as they move through the tool.">
            <div className="space-y-4">
              <div className="grid md:grid-cols-5 gap-2">
                {(builder.step_labels || []).map((s, i) => (
                  <label key={i} className="block">
                    <span className="text-[11px] font-semibold text-slate-400">Step {i + 1}</span>
                    <input
                      className={`${inputCls} mt-1`}
                      value={s}
                      data-testid={`package-step-label-${i}`}
                      onChange={(e) => setIn("builder", "step_labels", builder.step_labels.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </label>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Step 1 heading" value={builder.step1_heading} onChange={(v) => setIn("builder", "step1_heading", v)} />
                <Field label="Step 1 intro" value={builder.step1_intro} onChange={(v) => setIn("builder", "step1_intro", v)} area />
                <Field label="Step 2 heading" value={builder.step2_heading} onChange={(v) => setIn("builder", "step2_heading", v)} />
                <Field label="Step 2 intro" value={builder.step2_intro} onChange={(v) => setIn("builder", "step2_intro", v)} area />
                <Field label="Price step heading" value={builder.step3_heading} onChange={(v) => setIn("builder", "step3_heading", v)} />
                <Field label="Answer summary heading" value={builder.summary_heading} onChange={(v) => setIn("builder", "summary_heading", v)} />
                <Field label="Details step heading" value={builder.step4_heading} onChange={(v) => setIn("builder", "step4_heading", v)} />
                <Field label="Price panel — before a price appears" value={builder.rail_empty} onChange={(v) => setIn("builder", "rail_empty", v)} area />
                <Field label="Contact form heading" value={builder.contact_heading} onChange={(v) => setIn("builder", "contact_heading", v)} />
                <Field label="Contact form intro" value={builder.contact_intro} onChange={(v) => setIn("builder", "contact_intro", v)} area />
                <Field label="Booking heading" value={builder.booking_heading} onChange={(v) => setIn("builder", "booking_heading", v)} />
                <Field label="Booking intro" value={builder.booking_intro} onChange={(v) => setIn("builder", "booking_intro", v)} area />
              </div>
              <Field label="Thank-you message after they send their details" value={builder.submitted_message} onChange={(v) => setIn("builder", "submitted_message", v)} area />
            </div>
          </Card>

          <Card
            title="The offer of a call"
            subtitle="Shown once, on the price screen, to someone who looks like they're about to leave without doing anything. It offers a Discovery Call — never a different price."
          >
            <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="package-walkaway-enabled">
              <input
                type="checkbox"
                checked={walkaway.enabled !== false}
                onChange={(e) => setIn("walkaway_offer", "enabled", e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs text-navy">Show it. Turn this off and nobody is ever interrupted on the price screen.</span>
            </label>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Heading" value={walkaway.heading} onChange={(v) => setIn("walkaway_offer", "heading", v)} />
              <Field label="Button" value={walkaway.button_label} onChange={(v) => setIn("walkaway_offer", "button_label", v)} />
              <div className="md:col-span-2">
                <Field label="Message" value={walkaway.body} onChange={(v) => setIn("walkaway_offer", "body", v)} area rows={3} />
              </div>
              <Field label="The 'no thanks' wording" value={walkaway.dismiss_label} onChange={(v) => setIn("walkaway_offer", "dismiss_label", v)} />
            </div>
          </Card>


          <Card
            title="The guided journey"
            subtitle="How the redesigned journey divides the questions up. Presentation only — every question is still asked exactly once and sent to the pricing engine unchanged."
          >
            <div className="space-y-4">
              <Field
                label="Question groups shown on 'Your business'"
                value={csv(form.business_stage_groups)}
                onChange={(v) => set("business_stage_groups", list(v))}
                hint="Comma separated, matching the group names in the pricing tool. A question asked by more than one chosen service is treated as business-level automatically."
              />
              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label="Also move these questions there"
                  value={csv(form.business_stage_keys)}
                  onChange={(v) => set("business_stage_keys", list(v))}
                  hint="Question keys, comma separated."
                />
                <Field
                  label="Keep these questions out of it"
                  value={csv(form.business_stage_exclude_keys)}
                  onChange={(v) => set("business_stage_exclude_keys", list(v))}
                  hint="Question keys, comma separated. Wins over everything above."
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-navy mb-2">Services offered in the journey</p>
                {catalogue ? (
                  <div className="grid md:grid-cols-2 gap-2">
                    {(catalogue.services || []).map((s) => {
                      const off = (form.journey_excluded_service_ids || []).includes(s.service_id);
                      return (
                        <label key={s.service_id} className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-slate-50 p-3">
                          <input
                            type="checkbox"
                            checked={!off}
                            data-testid={`journey-service-${s.service_id}`}
                            onChange={(e) =>
                              set(
                                "journey_excluded_service_ids",
                                e.target.checked
                                  ? (form.journey_excluded_service_ids || []).filter((id) => id !== s.service_id)
                                  : [...(form.journey_excluded_service_ids || []), s.service_id],
                              )
                            }
                            className="mt-0.5"
                          />
                          <span className="text-xs text-navy font-semibold leading-snug">
                            {s.name}
                            <span className="block text-[11px] text-slate-400 font-normal">{s.category}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Loading the service list…</p>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  Unticking one keeps it in the pricing platform and in proposals — it simply isn't offered on the website.
                </p>
              </div>
            </div>
          </Card>

          <Card title="What a CASS package covers">
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Eyebrow" value={covers.eyebrow} onChange={(v) => setIn("covers", "eyebrow", v)} />
                <Field label="Title" value={covers.title} onChange={(v) => setIn("covers", "title", v)} />
                <Field label="Subtitle" value={covers.subtitle} onChange={(v) => setIn("covers", "subtitle", v)} />
              </div>
              <ListEditor
                items={covers.cards || []}
                onChange={(v) => setIn("covers", "cards", v)}
                makeEmpty={() => ({ icon: "Sparkles", title: "", body: "" })}
                addLabel="Add card"
                testPrefix="package-cover-card"
                renderItem={(item, patch) => (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-[160px,1fr] gap-3">
                      <label className="block">
                        <span className="text-xs font-semibold text-navy">Icon</span>
                        <select value={item.icon || "Sparkles"} onChange={(e) => patch({ ...item, icon: e.target.value })} className={`${inputCls} mt-1`}>
                          {iconNames.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </label>
                      <Field label="Title" value={item.title} onChange={(v) => patch({ ...item, title: v })} />
                    </div>
                    <Field label="Text" value={item.body} onChange={(v) => patch({ ...item, body: v })} area rows={3} />
                  </div>
                )}
              />
            </div>
          </Card>

          <Card title="How we price">
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Eyebrow" value={explainer.eyebrow} onChange={(v) => setIn("pricing_explainer", "eyebrow", v)} />
                <Field label="Title" value={explainer.title} onChange={(v) => setIn("pricing_explainer", "title", v)} />
              </div>
              <ListEditor
                items={explainer.paragraphs || []}
                onChange={(v) => setIn("pricing_explainer", "paragraphs", v)}
                makeEmpty={() => ""}
                addLabel="Add paragraph"
                testPrefix="package-explainer-para"
                renderItem={(item, patch) => <Field label="Paragraph" value={item} onChange={patch} area rows={4} />}
              />
            </div>
          </Card>

          <Card title="What happens on a Discovery Call">
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Eyebrow" value={discovery.eyebrow} onChange={(v) => setIn("discovery", "eyebrow", v)} />
                <Field label="Title" value={discovery.title} onChange={(v) => setIn("discovery", "title", v)} />
              </div>
              <ListEditor
                items={discovery.steps || []}
                onChange={(v) => setIn("discovery", "steps", v)}
                makeEmpty={() => ({ n: "", title: "", body: "" })}
                addLabel="Add step"
                testPrefix="package-discovery-step"
                renderItem={(item, patch) => (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-[100px,1fr] gap-3">
                      <Field label="Number" value={item.n} onChange={(v) => patch({ ...item, n: v })} />
                      <Field label="Title" value={item.title} onChange={(v) => patch({ ...item, title: v })} />
                    </div>
                    <Field label="Text" value={item.body} onChange={(v) => patch({ ...item, body: v })} area />
                  </div>
                )}
              />
            </div>
          </Card>

          <Card title="FAQs" subtitle="These appear on the page and are sent to Google as FAQ structured data, so keep the answers genuinely useful.">
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Eyebrow" value={form.faqs_eyebrow} onChange={(v) => set("faqs_eyebrow", v)} />
                <Field label="Section title" value={form.faqs_title} onChange={(v) => set("faqs_title", v)} />
              </div>
              <ListEditor
                items={form.faqs || []}
                onChange={(v) => set("faqs", v)}
                makeEmpty={() => ({ q: "", a: "" })}
                addLabel="Add FAQ"
                testPrefix="package-faq"
                renderItem={(item, patch) => (
                  <div className="space-y-3">
                    <Field label="Question" value={item.q} onChange={(v) => patch({ ...item, q: v })} />
                    <Field label="Answer" value={item.a} onChange={(v) => patch({ ...item, a: v })} area rows={4} />
                  </div>
                )}
              />
            </div>
          </Card>

          <Card title="Closing call to action">
            <div className="space-y-4">
              <Field label="Heading" value={cta.heading} onChange={(v) => setIn("cta", "heading", v)} />
              <Field label="Body" value={cta.body} onChange={(v) => setIn("cta", "body", v)} area rows={3} />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Button label" value={cta.button_label} onChange={(v) => setIn("cta", "button_label", v)} />
                <Field label="Button link" value={cta.button_link} onChange={(v) => setIn("cta", "button_link", v)} placeholder="/contact" />
              </div>
            </div>
          </Card>

          <Card title="What visitors see when the page is off" subtitle="Only used when visibility is set to 'Off completely'.">
            <div className="space-y-4">
              <Field label="Heading" value={unavailable.heading} onChange={(v) => setIn("unavailable", "heading", v)} />
              <Field label="Body" value={unavailable.body} onChange={(v) => setIn("unavailable", "body", v)} area rows={3} />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Button label" value={unavailable.button_label} onChange={(v) => setIn("unavailable", "button_label", v)} />
                <Field label="Button link" value={unavailable.button_link} onChange={(v) => setIn("unavailable", "button_link", v)} />
              </div>
            </div>
          </Card>

          <div className="pb-4">
            <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="package-page-save-bottom">
              {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
            </Button>
          </div>
        </>
      )}

      {tab === "services" && <PackageServicesTab />}

      {tab === "insights" && (
        <div data-testid="package-insights">
          {insights && t.sessions > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-slate-500">
                Based on <strong>{t.sessions}</strong> journey{t.sessions === 1 ? "" : "s"}
                {t.converted > 0 && <> · {t.converted} became {t.converted === 1 ? "an enquiry" : "enquiries"}</>}
              </p>
              <button
                type="button"
                onClick={() => setTab("journeys")}
                data-testid="insights-manage-journeys-btn"
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-orange hover:text-orange-dark text-slate-500 font-semibold text-sm px-4 py-2 rounded-full transition-colors"
              >
                <Users size={14} /> Remove a test journey
              </button>
            </div>
          )}

          {dropOff && dropOff.started > 0 && (
            <Card
              title="Where journeys stop"
              subtitle="Counts visitors who started choosing services but never reached a price, and the question they were looking at when they left. Anonymous — a stage and a question, nothing else."
            >
              <div className="grid sm:grid-cols-3 gap-3" data-testid="package-dropoff-totals">
                <Stat label="Started choosing" value={dropOff.started} />
                <Stat label="Reached a price" value={dropOff.priced} />
                <Stat label="Left before a price" value={dropOff.lost} />
              </div>

              {dropOff.lost === 0 ? (
                <p className="text-sm text-slate-600 mt-5">Everyone who started has reached a price — nobody has stopped part way.</p>
              ) : (
                <>
                  <div className="mt-6">
                    <p className="text-xs font-semibold text-navy mb-2">The stage they got to</p>
                    <div className="space-y-2">
                      {(dropOff.stages || []).map((s) => (
                        <div key={s.stage} className="flex items-center gap-3" data-testid={`package-dropoff-stage-${s.stage}`}>
                          <span className="text-xs text-slate-500 w-32 shrink-0">{s.name || `Stage ${s.stage}`}</span>
                          <span className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden">
                            <span className="bg-orange/70 h-full block" style={{ width: `${(s.left / maxLost) * 100}%` }} />
                          </span>
                          <span className="text-xs text-slate-400 w-10 text-right">{s.left}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(dropOff.questions || []).length > 0 && (
                    <div className="mt-6 overflow-x-auto">
                      <p className="text-xs font-semibold text-navy mb-2">The question they were on</p>
                      <table className="w-full text-sm" data-testid="package-dropoff-questions">
                        <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                          <th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Stage</th><th className="py-2 pr-3 text-right">Left here</th>
                        </tr></thead>
                        <tbody>
                          {dropOff.questions.map((q) => (
                            <tr key={q.key} className="border-b border-slate-50" data-testid={`package-dropoff-question-${q.key}`}>
                              <td className="py-2 pr-3 font-semibold text-navy">{q.label}</td>
                              <td className="py-2 pr-3 text-slate-500">{q.stage_name || "—"}</td>
                              <td className="py-2 pr-3 text-right text-slate-500">{q.left}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-[11px] text-slate-400 mt-2">
                        A question appears here when it was the next one waiting to be answered as the visitor left — so a question
                        near the top is either too much work, too confusing, or asking for something they don't have to hand.
                      </p>
                    </div>
                  )}
                </>
              )}

                {(dropOff.timings || []).length > 0 && (
                  <div className="mt-6 overflow-x-auto">
                    <p className="text-xs font-semibold text-navy mb-2">How long each question takes</p>
                    <table className="w-full text-sm" data-testid="package-timing-questions">
                      <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Stage</th>
                        <th className="py-2 pr-3 text-right">Average</th><th className="py-2 pr-3 text-right">Times seen</th><th className="py-2 pr-3 text-right">Left here</th>
                      </tr></thead>
                      <tbody>
                        {dropOff.timings.map((q) => (
                          <tr key={q.key} className="border-b border-slate-50" data-testid={`package-timing-${q.key}`}>
                            <td className="py-2 pr-3 font-semibold text-navy">{q.label}</td>
                            <td className="py-2 pr-3 text-slate-500">{q.stage_name || "—"}</td>
                            <td className="py-2 pr-3 text-right text-slate-500">{q.avg_seconds}s</td>
                            <td className="py-2 pr-3 text-right text-slate-500">{q.views}</td>
                            <td className="py-2 pr-3 text-right text-slate-500">{q.left_here || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[11px] text-slate-400 mt-2">
                      Longest first. A question that takes far longer than the rest is usually one people have to go and look
                      something up for — worth a clearer hint, a sensible default, or not asking it on the website at all.
                    </p>
                  </div>
                )}
            </Card>
          )}


          {!insights ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : t.sessions === 0 ? (
            <Card title="Nothing to show yet">
              <p className="text-sm text-slate-600 leading-relaxed">
                No one has built a package yet. As soon as visitors start using it, this screen will show which services get chosen
                together, the prices people walk away from, and how many go on to leave their details. Nothing here is estimated or
                made up — it's only ever real journeys.
              </p>
            </Card>
          ) : (
            <>
              <Card title="The funnel" subtitle="A journey is recorded the moment a visitor gets their first price, so these figures describe people who got that far. They don't claim to count anyone who left at the service list.">
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  <Stat label="Got a price" value={t.priced || 0} />
                  <Stat label="Left their details" value={t.converted || 0} />
                  <Stat label="Walked away" value={t.walked_away || 0} hint="Priced, never converted" />
                  <Stat label="Needed a call" value={t.consultation || 0} hint="Couldn't be priced online" />
                  <Stat label="Conversion" value={`${t.conversion_rate || 0}%`} hint="Of those who saw a price" />
                </div>
              </Card>

              <Card title="Prices people walk away from" subtitle="The single most useful number here: what the people who didn't come back were quoted, against what the people who did were quoted.">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Stat label="Average walked away" value={money(w.avg_walked_away)} />
                  <Stat label="Median walked away" value={money(w.median_walked_away)} />
                  <Stat label="Average converted" value={money(w.avg_converted)} />
                  <Stat label="Median converted" value={money(w.median_converted)} />
                </div>
                <div className="mt-6 space-y-2">
                  {(w.bands || []).map((band) => {
                    const total = band.walked_away + band.converted;
                    return (
                      <div key={band.label} className="flex items-center gap-3" data-testid={`package-band-${band.label}`}>
                        <span className="text-xs text-slate-500 w-24 shrink-0">{band.label}</span>
                        <span className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden flex">
                          <span className="bg-navy h-full" style={{ width: `${(band.converted / maxBand) * 100}%` }} title={`${band.converted} left details`} />
                          <span className="bg-orange/70 h-full" style={{ width: `${(band.walked_away / maxBand) * 100}%` }} title={`${band.walked_away} walked away`} />
                        </span>
                        <span className="text-xs text-slate-400 w-10 text-right">{total || ""}</span>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-slate-400 mt-2">
                    <span className="inline-block w-2 h-2 rounded-sm bg-navy align-middle mr-1" /> left their details
                    <span className="inline-block w-2 h-2 rounded-sm bg-orange/70 align-middle ml-4 mr-1" /> walked away
                  </p>
                </div>
                {(w.highest || []).length > 0 && (
                  <div className="mt-6 overflow-x-auto">
                    <p className="text-xs font-semibold text-navy mb-2">Biggest quotes nobody came back for</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3">Monthly</th><th className="py-2 pr-3">One-off</th><th className="py-2 pr-3">Services</th><th className="py-2 pr-3">When</th>
                      </tr></thead>
                      <tbody>
                        {w.highest.map((r) => (
                          <tr key={r.session_id} className="border-b border-slate-50">
                            <td className="py-2 pr-3 font-semibold text-navy">{money(r.monthly_net)}</td>
                            <td className="py-2 pr-3">{r.one_off_net ? money(r.one_off_net) : "—"}</td>
                            <td className="py-2 pr-3 text-slate-500">{(r.services || []).join(", ") || "—"}</td>
                            <td className="py-2 pr-3 text-slate-400">{(r.updated_at || "").slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              <Card title="Services chosen together" subtitle="Pairs that keep appearing together are your natural packages — and a hint at what to put side by side on the site.">
                {(insights.pairs || []).length === 0 ? (
                  <p className="text-sm text-slate-400">Not enough journeys yet to see pairs.</p>
                ) : (
                  <div className="space-y-2">
                    {insights.pairs.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm" data-testid={`package-pair-${i}`}>
                        <span className="flex-1 text-navy">{p.a} <span className="text-slate-300">+</span> {p.b}</span>
                        <span className="h-2 bg-orange/60 rounded-full" style={{ width: `${(p.count / insights.pairs[0].count) * 40}%` }} />
                        <span className="text-slate-400 w-8 text-right">{p.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Most wanted services">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-3">Service</th><th className="py-2 pr-3">Chosen</th><th className="py-2 pr-3">Left details</th><th className="py-2 pr-3">Conversion</th>
                    </tr></thead>
                    <tbody>
                      {(insights.services || []).map((s) => (
                        <tr key={s.name} className="border-b border-slate-50">
                          <td className="py-2 pr-3 text-navy">{s.name}</td>
                          <td className="py-2 pr-3">{s.count}</td>
                          <td className="py-2 pr-3">{s.converted}</td>
                          <td className="py-2 pr-3 text-slate-500">{s.conversion_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {insights.catalogue_available === false && (
                  <p className="text-[11px] text-orange-dark mt-3">
                    Service names couldn't be loaded from the pricing tool just now, so some rows may show a short reference instead.
                  </p>
                )}
              </Card>
            </>
          )}

          {(t.sessions > 0 || (dropOff?.started || 0) > 0) && (
            <Card
              title="Start the figures again"
              subtitle="Clears every journey the website has recorded, along with the funnel, the drop-off and the timings. Your page content, service rules and everything in the CASS pricing tool are untouched — and nothing that's already reached your pipeline is affected."
            >
              {confirmReset ? (
                <div className="flex flex-wrap items-center gap-3" data-testid="package-reset-confirm">
                  <p className="text-sm text-navy font-semibold w-full">
                    Delete all {t.sessions || 0} recorded journey{(t.sessions || 0) === 1 ? "" : "s"} and start from zero? This can't be undone.
                  </p>
                  <button
                    type="button"
                    onClick={resetInsights}
                    disabled={resetting}
                    data-testid="package-reset-yes"
                    className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white duration-200 [transition-property:background-color,opacity] hover:bg-red-700 disabled:opacity-60"
                  >
                    <Trash2 size={15} /> {resetting ? "Clearing…" : "Yes, clear it all"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmReset(false)}
                    className="text-sm font-semibold text-slate-500 hover:text-navy duration-200 [transition-property:color]"
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmReset(true)}
                  data-testid="package-reset-insights"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-navy duration-200 [transition-property:border-color,background-color] hover:border-red-300 hover:bg-red-50"
                >
                  <Trash2 size={15} /> Reset insights
                </button>
              )}
            </Card>
          )}

        </div>
      )}

      {tab === "journeys" && (
        <Card title={`Journeys (${journeys.length})`} subtitle="Every package built, newest first. Click the eye to see exactly what a visitor asked for and print it. Delete your own test runs line by line — each one you remove drops straight out of the Insights figures. Unconverted journeys hold no personal details and are deleted automatically after 12 months.">
          {journeys.length === 0 ? (
            <p className="text-sm text-slate-400 flex items-center gap-2"><Users size={15} /> No journeys yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Who</th><th className="py-2 pr-3">Services</th>
                  <th className="py-2 pr-3">Monthly</th><th className="py-2 pr-3">One-off</th><th className="py-2 pr-3">Outcome</th><th></th>
                </tr></thead>
                <tbody>
                  {journeys.map((j) => (
                    <tr key={j.session_id} className="border-b border-slate-50" data-testid={`package-journey-${j.session_id}`}>
                      <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{(j.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 pr-3 text-navy">
                        {j.full_name ? (
                          <>
                            <span className="font-semibold">{j.full_name}</span>
                            {j.business_name && <span className="block text-xs text-slate-400">{j.business_name}</span>}
                            {j.email && <span className="block text-xs text-slate-400">{j.email}</span>}
                          </>
                        ) : <span className="text-slate-400">Anonymous</span>}
                      </td>
                      <td className="py-2 pr-3 text-slate-500 max-w-[260px]">{(j.services || []).join(", ") || "—"}</td>
                      <td className="py-2 pr-3">{money(j.monthly_net)}</td>
                      <td className="py-2 pr-3">{j.one_off_net ? money(j.one_off_net) : "—"}</td>
                      <td className="py-2 pr-3">
                        {j.converted ? (
                          <span className="text-[11px] font-bold uppercase bg-teal/10 text-teal px-2 py-0.5 rounded">Lead{j.pipeline_sent ? " · synced" : ""}</span>
                        ) : j.status === "consultation" ? (
                          <span className="text-[11px] font-bold uppercase bg-navy/5 text-navy px-2 py-0.5 rounded">Needs a call</span>
                        ) : (
                          <span className="text-[11px] font-bold uppercase bg-orange/10 text-orange-dark px-2 py-0.5 rounded">Walked away</span>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setViewId(j.session_id)} className="text-slate-400 hover:text-navy" title="See what they asked for" data-testid={`package-journey-view-${j.session_id}`}><Eye size={16} /></button>
                          {confirmId === j.session_id ? (
                          <span className="inline-flex items-center gap-2 whitespace-nowrap">
                            <span className="text-xs text-slate-500">Delete?</span>
                            <button
                              onClick={() => removeJourney(j.session_id)}
                              disabled={deletingId === j.session_id}
                              data-testid={`package-journey-confirm-${j.session_id}`}
                              className="text-xs font-bold text-red-600 hover:text-red-700 disabled:opacity-60"
                            >
                              {deletingId === j.session_id ? "Deleting…" : "Yes"}
                            </button>
                            <button
                              onClick={() => setConfirmId(null)}
                              data-testid={`package-journey-cancel-${j.session_id}`}
                              className="text-xs font-semibold text-slate-400 hover:text-navy"
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmId(j.session_id)} className="text-red-400 hover:text-red-600" data-testid={`package-journey-remove-${j.session_id}`}><Trash2 size={15} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "insights" && insights && t.sessions > 0 && (
        <p className="text-xs text-slate-400 flex items-center gap-2 mb-8"><BarChart3 size={13} /> Based on {t.sessions} recorded journey{t.sessions === 1 ? "" : "s"}.</p>
      )}

      {viewId && <PackageJourneyModal sessionId={viewId} onClose={() => setViewId(null)} />}
    </div>
  );
}
